import React, { useState } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, FormControlLabel, Switch, FormHelperText } from '@mui/material';
import { PayerType } from '../../../../services/ConfigurationService/types/payerType';
import { useGetPayerGroupsQuery, useUpsertPayerMutation } from '../../../../services/ConfigurationService';
import { useGetCalcTypesQuery } from '../../../../services/ContractService';
import { useLocation } from 'react-router-dom';

interface AddPayerDialogProps {
  defaultCenterId: string;
  defaultCompany: string;
  onAdd?: (payer: PayerType) => void | Promise<void>;
}

export const AddPayerDialog: React.FC<AddPayerDialogProps> = ({ defaultCenterId, defaultCompany, onAdd }) => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const companyFromUrl = query.get('company') || defaultCompany;
  const [open, setOpen] = useState(false);
  const { data: calcTypes = [] } = useGetCalcTypesQuery();
  const { data: payerGroups = [], isLoading: payerGroupsLoading } = useGetPayerGroupsQuery();

  const buildCompositePayerId = (company: string, centerId: string, payerCode: string): string => {
    const normalizedCompany = company.trim();
    const normalizedCenter = centerId.trim();
    const normalizedPayerCode = payerCode.trim();

    if (!normalizedCompany)
      return `${normalizedCenter}-${normalizedPayerCode}`;

    const expectedPrefix = `${normalizedCompany}-`;
    const centerWithoutCompany = normalizedCenter.toUpperCase().startsWith(expectedPrefix.toUpperCase())
      ? normalizedCenter.substring(expectedPrefix.length)
      : normalizedCenter;

    return `${normalizedCompany}-${centerWithoutCompany}-${normalizedPayerCode}`;
  };

  const [payer, setPayer] = useState<PayerType>({
    id: '',
    centerId: defaultCenterId,
    payerCode: '',
    payerName: '',
    evaEnabled: false,
    payerGroup: '',
    system: '',
    financialClass: '',
    portal: '',
    phone: '',
    ecsPayerId: '',
    waystarAuthPayer: '',
    company: companyFromUrl,
  });
  const [payerGroupError, setPayerGroupError] = useState(false);
  const [upsertPayer] = useUpsertPayerMutation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPayer({ ...payer, [e.target.name]: e.target.value, company: companyFromUrl });
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    setPayer({ ...payer, [e.target.name]: e.target.value, company: companyFromUrl });
  };

  const handleEvaEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setPayer({ ...payer, evaEnabled: checked, company: companyFromUrl });
    if (!checked)
      setPayerGroupError(false);
  };

  const handleSave = async () => {
    if (payer.evaEnabled && !payer.payerGroup.trim())
    {
      setPayerGroupError(true);
      return;
    }

    setPayerGroupError(false);

    // Set id as centerId-payerCode before saving
    const newPayer = {
      ...payer,
      id: buildCompositePayerId(companyFromUrl, payer.centerId, payer.payerCode),
      company: companyFromUrl,
    };
    await upsertPayer(newPayer);
    if (onAdd) await onAdd(newPayer);
    setOpen(false);
    setPayer({
      id: '',
      centerId: defaultCenterId,
      payerCode: '',
      payerName: '',
      evaEnabled: false,
      payerGroup: '',
      system: '',
      financialClass: '',
      portal: '',
      phone: '',
      ecsPayerId: '',
      waystarAuthPayer: '',
      company: companyFromUrl,
    });
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Add Payer
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Payer</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Payer Code" name="payerCode" value={payer.payerCode} onChange={handleChange} fullWidth />
            <TextField label="Payer Name" name="payerName" value={payer.payerName} onChange={handleChange} fullWidth />
            <TextField label="System" name="system" value={payer.system} onChange={handleChange} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Financial Class</InputLabel>
              <Select
                name="financialClass"
                value={payer.financialClass}
                onChange={handleSelectChange}
                label="Financial Class"
              >
                {calcTypes.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Portal" name="portal" value={payer.portal} onChange={handleChange} fullWidth />
            <TextField label="Phone" name="phone" value={payer.phone} onChange={handleChange} fullWidth />
            <TextField label="ECS Payer ID" name="ecsPayerId" value={payer.ecsPayerId} onChange={handleChange} fullWidth />
            <TextField label="Waystar Auth Payer" name="waystarAuthPayer" value={payer.waystarAuthPayer} onChange={handleChange} fullWidth />
            <FormControlLabel
              control={<Switch checked={payer.evaEnabled} onChange={handleEvaEnabledChange} color="primary" />}
              label="EVA Enabled"
            />
            <FormControl fullWidth error={payerGroupError}>
              <InputLabel>Payer Group</InputLabel>
              <Select
                name="payerGroup"
                value={payer.payerGroup}
                onChange={handleSelectChange}
                label="Payer Group"
                disabled={!payer.evaEnabled || payerGroupsLoading}
              >
                {payerGroups.map((group) => (
                  <MenuItem key={group.value} value={group.value}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
              {payerGroupError ? <FormHelperText>Payer Group is required when EVA is enabled.</FormHelperText> : null}
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

